export type NativeBackFormState = 'ready' | 'dirty' | 'busy';

/** Ignore mounted forms in closed dialogs or hidden responsive surfaces. */
const isVisible = (node: Element): boolean => {
  for (let parent: Element | null = node; parent; parent = parent.parentElement) {
    if (
      parent.hasAttribute('hidden') ||
      (parent.getAttribute('data-state') === 'closed' &&
        ['dialog', 'alertdialog'].includes(parent.getAttribute('role') ?? '')) ||
      getComputedStyle(parent).display === 'none' ||
      getComputedStyle(parent).visibility === 'hidden'
    )
      return false;
  }
  return true;
};

export const nativeBackFormState = (scope: Element): NativeBackFormState => {
  const nodes = [
    scope,
    ...scope.querySelectorAll('[data-native-back-dirty], [data-native-back-busy]'),
  ].filter(isVisible);
  if (nodes.some((node) => node.getAttribute('data-native-back-busy') === 'true')) return 'busy';
  return nodes.some((node) => node.getAttribute('data-native-back-dirty') === 'true')
    ? 'dirty'
    : 'ready';
};

const DIALOGS = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';
const MENUS = [
  ...['menu', 'select', 'combobox', 'popover'].map(
    (scope) => `[data-scope="${scope}"][data-part="content"][data-state="open"]`
  ),
  '[data-native-back-picker="true"]',
].join(',');

/** A nested picker consumes Back without discarding the form underneath it. */
const lastVisible = (selector: string): Element | undefined => {
  const nodes = document.querySelectorAll(selector);
  for (let index = nodes.length - 1; index >= 0; index--) {
    if (isVisible(nodes[index])) return nodes[index];
  }
  return undefined;
};

export const nativeBackOverlay = (): { node: Element; formBoundary: boolean } | null => {
  const picker = lastVisible(MENUS);
  if (picker) return { node: picker, formBoundary: false };
  const dialog = lastVisible(DIALOGS);
  return dialog ? { node: dialog, formBoundary: true } : null;
};

export const nativeBackPath = (pathname: string): string =>
  pathname.replace(/^\/(ru|en)(?=\/|$)/, '').replace(/\/$/, '') || '/';

export const isNativeBackRoot = (pathname: string): boolean =>
  [
    '/',
    '/dashboard',
    '/dashboard/discover',
    '/dashboard/chats',
    '/dashboard/friends',
    '/dashboard/settings',
    '/sign-in',
    '/sign-up',
    '/forgot-password',
    '/reset-password',
  ].includes(nativeBackPath(pathname));

export const nativeBackFallback = (
  pathname: string
): '/{-$locale}/dashboard/settings' | '/{-$locale}/dashboard/discover' =>
  nativeBackPath(pathname).startsWith('/dashboard/settings/')
    ? '/{-$locale}/dashboard/settings'
    : '/{-$locale}/dashboard/discover';
